import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useSchemaCatalogue } from '@/features/schema/hooks/useSchemaCatalogue'
import type { CatalogueType, SchemaCatalogue } from '@/services/api/schemaCatalogue'

export function CatalogueBrowser() {
  const { t } = useTranslation()
  const query = useSchemaCatalogue()
  if (query.isPending) return <p role="status">{t('schemaAgent.kickoff.loading')}</p>
  if (query.isError)
    return (
      <div role="alert">
        <p>{t('schemaAgent.catalogue.error')}</p>
        <Button variant="outline" onClick={() => void query.refetch()}>
          {t('schemaAgent.error.retry')}
        </Button>
      </div>
    )
  return <CatalogueView catalogue={query.data} />
}

export function CatalogueView({ catalogue }: { catalogue: SchemaCatalogue }) {
  const { t } = useTranslation()
  const [domain, setDomain] = useState('all')
  const [search, setSearch] = useState('')
  const matches = (item: { domains: string[] }, text: string) =>
    (domain === 'all' || item.domains.includes(domain)) &&
    text.toLowerCase().includes(search.toLowerCase())
  return (
    <div className="space-y-4 pt-3">
      <div className="flex flex-wrap gap-2">
        <Input
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder={t('schemaAgent.catalogue.search')}
          className="flex-1 min-w-40 max-md:basis-full"
        />
        <Select value={domain} onValueChange={setDomain}>
          <SelectTrigger className="w-48 max-md:flex-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('schemaAgent.catalogue.all')}</SelectItem>
            {catalogue.domains.map(item => (
              <SelectItem key={item.id} value={item.id}>
                {item.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {domain !== 'all' && (
        <p className="text-sm text-muted-foreground">
          {catalogue.domains.find(item => item.id === domain)?.description}
        </p>
      )}
      {(['entities', 'relationships'] as const).map(kind => (
        <section key={kind} className="space-y-2">
          <h3 className="font-medium text-sm">{t(`schemaAgent.catalogue.${kind}`)}</h3>
          {catalogue[kind]
            .filter(item =>
              matches(
                item,
                `${item.label} ${item.description} ${item.properties.map(property => property.name).join(' ')}`
              )
            )
            .map(item => (
              <CatalogueTypeRow key={item.label} item={item} />
            ))}
        </section>
      ))}
      <section className="space-y-2">
        <h3 className="font-medium text-sm">{t('schemaAgent.editor.patterns')}</h3>
        {catalogue.patterns
          .filter(item =>
            matches(item, `${item.source} ${item.relationship} ${item.target} ${item.description}`)
          )
          .map(item => (
            <div
              key={`${item.source}:${item.relationship}:${item.target}`}
              className="rounded-md border p-3 text-xs"
            >
              <p className="font-mono">
                {item.source} → {item.relationship} → {item.target}
              </p>
              <p className="text-muted-foreground mt-1">{item.description}</p>
            </div>
          ))}
      </section>
    </div>
  )
}

function CatalogueTypeRow({ item }: { item: CatalogueType }) {
  return (
    <details className="rounded-md border p-3 text-sm">
      <summary className="cursor-pointer">
        <span className="font-mono font-medium">{item.label}</span>{' '}
        <span className="text-muted-foreground">{item.description}</span>
      </summary>
      <div className="space-y-2 pt-3">
        <div className="flex flex-wrap gap-1">
          {item.domains.map(domain => (
            <Badge variant="secondary" key={domain}>
              {domain}
            </Badge>
          ))}
        </div>
        {item.platform_bound && (
          <p className="text-xs text-muted-foreground">
            {item.platform_bound.feature}: {item.platform_bound.why}
          </p>
        )}
        {item.properties.map(property => (
          <div key={property.name} className="border-t pt-2 text-xs">
            <span className="font-mono">
              {property.name}: {property.type}
            </span>
            <p className="text-muted-foreground">{property.description}</p>
            {property.platform_bound && (
              <p>
                {property.platform_bound.feature}: {property.platform_bound.why}
              </p>
            )}
          </div>
        ))}
      </div>
    </details>
  )
}
